import { db, storage } from "@/firebaseApp";
import {
    collection,
    deleteDoc,
    doc,
    getCountFromServer,
    getDoc,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import {
    ref,
    uploadBytesResumable,
    getDownloadURL
} from "firebase/storage";

function projectSetup(getters, commit, worker) {
    let projects = getters['projects/worker'](worker.id)
    for (let project of projects) {
        if (project.workerName == worker.name) {
            continue
        }
        project.workerName = worker.name
        commit("projects/add", project, { root: true })
    }
    return
}

const workers = {
    namespaced: true,
    state: {
        all: [],
        worker: null,
        unsubs: [],
    },

    getters: {
        email: (state) => (workerEmail) => {
            return state.all.find(({ email }) => email == workerEmail)
        },
        id: (state) => (workerId) => {
            return state.all.find(({ id }) => id == workerId)
        },
        invited: (state) => (name, email) => {
            return state.all.find((worker) => {
                return worker.email == email &&
                worker.name == name &&
                worker.state == 'invited'
            })
        },
    },

    mutations: {
        add(state, newWorker) {
            state.all.push(newWorker);
        },
        addUnsub(state, unsub) {
            state.unsubs.push(unsub)
        },
        worker(state, worker) {
            state.worker = worker;
        },
        update(state, updatedWorker) {
            const index = state.all.findIndex(worker => worker.id === updatedWorker.id);
            if (index !== -1) {
                state.all.splice(index, 1, updatedWorker);
            }
        },
        setCurrentUser(state, worker) {
            state.currentUser = worker
        },
        setErrorCode(state, error) {
            state.errorCode = error
        },
        setRecoverEmailSent(state, recoverEmailSent) {
            state.recoverEmailSent = recoverEmailSent
        },
        setWorkers(state, workers) {
            state.workers = workers;
        },
        delete(state, worker) {
            const index = state.all.findIndex(({ id }) => worker.id === id)
            if (index < 0) {
                return
            }
            state.all.splice(index, 1)
        },
        unsub(state) {
            while (state.unsubs.length) {
                let unsub = state.unsubs.pop()
                unsub()
            }
        },
        setCurrentUserRole(state, role) {
            state.role = role;
        },
    },

    actions: {
        addService({ rootState }, { workerId, serviceId }) {
            const instanceId = rootState.instanceId
            if (instanceId == null) return
            const currentUser = rootState.users.currentUser
            if (currentUser == null) return

            const workerRef = doc(collection(db, `instances/${instanceId}/workers`), workerId)
            updateDoc(workerRef, { service: serviceId })
        },

        async create({ rootState }, { name, email, logo }) {
            const instanceId = rootState.instanceId
            const currentUser = rootState.users.currentUser

            if (instanceId == null) return
            if (currentUser == null) return

            try {
                const workerRef = doc(collection(db, `instances/${instanceId}/workers`))
                if (!logo) {
                    setDoc(workerRef, {
                        name,
                        email,
                        createdAt: serverTimestamp(),
                        createdBy: currentUser.uid,
                    })
                    return
                }
                const uploadRef = ref(
                    storage,
                    `${instanceId}/workers/${workerRef.id}/logo`
                )
                const uploadTask = uploadBytesResumable(uploadRef, logo)
                uploadTask.on('state_changed',
                    (snapshot) => {
                        console.log(snapshot.bytesTransferred / snapshot.totalBytes * 100)
                        console.log(snapshot.state)
                    },
                    (error) => {

                        console.log(error)
                    },
                    () => {
                        setDoc(workerRef, {
                            name,
                            email,
                            createdAt: serverTimestamp(),
                            createdBy: currentUser.uid,
                        })
                    })
            } catch (error) {
                console.log(error);
            }
        },

        getAll({ commit, rootState, rootGetters }) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            commit("unsub")
            try {
                let unsub = onSnapshot(collection(db, `instances/${instanceId}/workers`), (querySnapshot) => {
                    querySnapshot.docChanges().forEach(async (change) => {
                        const data = change.doc.data();
                        const q = query(collection(db,`instances/${instanceId}/users`), where('worker', '==', change.doc.id))
                        let worker = {
                            ...data,
                            id: change.doc.id,
                            registered: (await getCountFromServer(q)).data().count.toString(),
                        }
                        try {
                            const fileRef = ref(
                                storage,
                                `${instanceId}/workers/${change.doc.id}/logo`
                            )
                            worker.logoUrl = await getDownloadURL(fileRef)
                        } catch (error) {
                            console.log("No Logo Found" + error);
                        }
                        projectSetup(rootGetters, commit, worker)
                        switch (change.type) {
                            case "added":
                                commit("delete", worker);
                                commit("add", worker);
                                break;
                            case "modified":
                                commit("delete", worker);
                                commit("add", worker);
                                break;
                            case "removed":
                                commit("delete", worker);
                                break;
                            default:
                                console.log(change.type)
                                break;
                        }
                    });
                })
                commit('addUnsub', unsub)
            } catch (error) {
                console.log("ERROR TO GET ALL Workers: " + error);
            }
        },

        async get({ commit, rootState }, workerId) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            try {
                let docSnap = await getDoc(doc(db, `instances/${instanceId}/workers/${workerId}`));
                if (!docSnap.exists()) {
                    console.log("NOT EXIST");
                    return
                }
                const worker = {
                    ...docSnap.data(),
                    id: docSnap.id,
                }
                commit("delete", worker);
                commit("add", worker);
            } catch (error) {
                console.log(`ERROR GETTING WORKER ${workerId}: ` + error);
            }
        },
        async addProjectCollection({ rootState }, { userId, name }) {
            const instanceId = rootState.instanceId
            const role = rootState.users.role

            if (instanceId == null) return;
            if (role !== 'consumer') return

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/users/${userId}/projects`))
                await setDoc(projectRef, {
                    name,
                    createdAt: serverTimestamp(),
                    createdBy: rootState.users.currentUser.uid,
                })
            } catch (error) {
                console.log(error)
            }
        },

        async delete({ commit, rootState }, id) {
            const instanceId = rootState.instanceId

            try {
                const workersRef = doc(db, `instances/${instanceId}/workers`, id)
                await deleteDoc(workersRef)
                commit('delete', id)

            } catch (error) {
                console.log('ERROR DELETING SERVICE', error)
            }
        },

        async edit({ commit, rootState }, { worker }) {
            const instanceId = rootState.instanceId
            try {
                const workerRef = doc(db, `instances/${instanceId}/workers`, worker.id);
                await updateDoc(workerRef, {
                    name: worker.name,
                    role: worker.role
                });
                commit('update', worker)
            } catch (error) {
                console.log(error)
            }
        },
    },
};

export default workers;
